import { RefObject, useCallback, useEffect, useState } from 'react'

type VirtualRows = {
  /** First and last (exclusive) row index to render. */
  start: number
  end: number
  /** Height to reserve before/after the rendered rows. */
  paddingTop: number
  paddingBottom: number
  /** Attach to any rendered row so the real row height is measured. */
  measureRow: (row: HTMLElement | null) => void
}

/**
 * Renders only the rows visible inside a scrolling container (plus a margin),
 * so long tables stay fast. Rows must have a uniform height.
 */
export const useVirtualRows = (
  scrollRef: RefObject<HTMLElement>,
  count: number,
  { estimatedRowHeight = 45, overscan = 10 } = {}
): VirtualRows => {
  const [rowHeight, setRowHeight] = useState(estimatedRowHeight)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportHeight, setViewportHeight] = useState(0)

  useEffect(() => {
    const element = scrollRef.current

    if (!element) {
      return
    }

    const update = () => {
      setScrollTop(element.scrollTop)
      setViewportHeight(element.clientHeight)
    }

    update()
    element.addEventListener('scroll', update, { passive: true })

    const observer =
      'ResizeObserver' in window ? new ResizeObserver(update) : null
    observer?.observe(element)

    return () => {
      element.removeEventListener('scroll', update)
      observer?.disconnect()
    }
  }, [scrollRef])

  const measureRow = useCallback((row: HTMLElement | null) => {
    const height = row?.getBoundingClientRect().height

    if (height && Math.abs(height - rowHeight) > 0.5) {
      setRowHeight(height)
    }
  }, [rowHeight])

  // Until the container has been measured, render a first screenful.
  const visibleHeight = viewportHeight || rowHeight * 20
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
  const end = Math.min(
    count,
    Math.ceil((scrollTop + visibleHeight) / rowHeight) + overscan
  )

  return {
    start,
    end,
    paddingTop: start * rowHeight,
    paddingBottom: Math.max(0, (count - end) * rowHeight),
    measureRow,
  }
}
